import type { ApplicationInsights } from '@microsoft/applicationinsights-web';
import * as Sentry from '@sentry/react';

let ai: ApplicationInsights | null = null;
let enabled = false;
// Set as soon as init() commits to loading, so repeated/concurrent calls don't
// each kick off a dynamic import before the first one resolves.
let initStarted = false;

// The App Insights SDK (~40-60KB gzip) is dynamic-imported so it lands in its
// own chunk and never blocks the app entry; init() is called after first paint. (#260)
async function init(): Promise<void> {
  if (initStarted) return; // idempotent (guards concurrent async calls too)
  const connectionString = import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING as string | undefined;
  if (!connectionString) { enabled = false; return; }
  initStarted = true;
  try {
    const { ApplicationInsights } = await import('@microsoft/applicationinsights-web');
    const instance = new ApplicationInsights({
      config: {
        connectionString,
        disableAjaxTracking: true,
        disableFetchTracking: true,
        enableUnhandledPromiseRejectionTracking: true,
        enableAutoRouteTracking: false,
        autoTrackPageVisitTime: false,
      },
    });
    instance.loadAppInsights();
    ai = instance;
    enabled = true;
  } catch (err) {
    // Never let a telemetry load failure surface to the user.
    console.warn('telemetry: failed to load Application Insights', err);
    initStarted = false; // allow a later retry
  }
}

function trackError(error: unknown, context?: Record<string, string>): void {
  if (!enabled || !ai) return;
  const exception = error instanceof Error ? error : new Error(String(error));
  ai.trackException({ exception, properties: context });
}

function trackEvent(name: string, props?: Record<string, string>): void {
  if (!enabled || !ai) return;
  ai.trackEvent({ name }, props);
}

function trackPageView(name?: string): void {
  if (!enabled || !ai) return;
  ai.trackPageView(name ? { name } : {});
}

/** Optional display identity mirrored into Sentry (feedback attribution). */
export interface TelemetryUser {
  name?: string;
  email?: string;
}

function setAuthenticatedUser(userId: string, tenantId?: string, user?: TelemetryUser): void {
  // Sentry identity: pre-fills the User Feedback widget's name/email and
  // attributes events. setUser is a no-op when the SDK is uninitialized.
  Sentry.setUser({
    id: userId,
    ...(user?.email ? { email: user.email, username: user.email } : {}),
    ...(user?.name ? { name: user.name } : {}),
  });
  if (!enabled || !ai) return;
  ai.setAuthenticatedUserContext(userId, tenantId, true);
}

function clearAuthenticatedUser(): void {
  Sentry.setUser(null);
  if (!enabled || !ai) return;
  ai.clearAuthenticatedUserContext();
}

export const telemetry = {
  init,
  trackError,
  trackEvent,
  trackPageView,
  setAuthenticatedUser,
  clearAuthenticatedUser,
  get enabled(): boolean { return enabled; },
};
